import { useState } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { X, Copy, Printer, QrCode } from "@phosphor-icons/react";

/**
 * Unified "Ledger Tools" dialog: QR tag + Heirs share link.
 * Used from AssetDetail and Dashboard.
 */
export default function ShareQrDialog({ open, onClose, assetId, assetName, serial }) {
  const [mode, setMode] = useState("qr"); // "qr" | "share"
  const [generating, setGenerating] = useState(false);
  const [share, setShare] = useState(null);

  if (!open) return null;

  const qrUrl = assetId ? `${API}/assets/${assetId}/qr?size=12&base=${encodeURIComponent(window.location.origin)}` : null;
  const publicAssetUrl = assetId
    ? `${window.location.origin}/assets/${assetId}`
    : null;

  const createShare = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/share-links", {
        label: "For Heirs & Executors",
        include_dispensation: true,
        include_valuation: true,
        include_photos: true,
      });
      setShare(r.data);
    } catch {
      toast.error("Could not create share link.");
    } finally {
      setGenerating(false);
    }
  };

  const copy = (text) => {
    try {
      navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard.");
    } catch {
      toast.error("Copy failed.");
    }
  };

  const printQr = () => {
    if (!qrUrl) return;
    // NOTE on `window.open` features: we DELIBERATELY do NOT pass
    // "noopener,noreferrer" here. Per HTML spec, `noopener` (which is
    // also implied by `noreferrer`) forces `window.open` to return null.
    // We need the window reference to populate the popup's DOM
    // ourselves — the popup starts as an about:blank same-origin
    // document that never navigates to an untrusted URL, so there is
    // no cross-origin opener risk to isolate from.
    const win = window.open("", "_blank");
    if (!win) {
      toast.error("Please allow popups to print the QR tag.");
      return;
    }
    const doc = win.document;
    // Build the print document with safe DOM APIs — every user-controlled
    // value (asset name, serial) goes through textContent, never
    // interpolated into an HTML string. Prior implementation used
    // document.write with template literals, which allowed a malicious
    // asset name like `</title><script>alert(1)</script>` to execute JS
    // in the popup context (v3.14.5 security-audit fix).
    doc.title = `${assetName || "Asset"} — QR Label`;
    const style = doc.createElement("style");
    style.textContent = `
      @page { size: auto; margin: 8mm; }
      body { font-family: Georgia, serif; color: #0A192F; padding: 20px; }
      .label { border: 1px solid #C9C4B8; padding: 24px; max-width: 360px; text-align: center; }
      .eyebrow { font-family: monospace; font-size: 9px; letter-spacing: 0.25em; color: #5C5F58; text-transform: uppercase; margin-bottom: 8px; }
      h1 { font-size: 22px; margin: 4px 0 12px; font-weight: 400; }
      .serial { font-family: monospace; font-size: 10px; color: #5C5F58; margin-top: 12px; }
      img { width: 220px; height: 220px; }
    `;
    doc.head.appendChild(style);

    const label = doc.createElement("div");
    label.className = "label";

    const eyebrow = doc.createElement("div");
    eyebrow.className = "eyebrow";
    eyebrow.textContent = "The Asset Arbor";
    label.appendChild(eyebrow);

    const h1 = doc.createElement("h1");
    h1.textContent = assetName || "Asset";
    label.appendChild(h1);

    const img = doc.createElement("img");
    img.src = qrUrl;
    img.alt = "QR code";
    // Print only after the QR image has loaded, so the preview isn't
    // blank on slow connections. Fall back to a 1.5-second timeout
    // when the load event doesn't fire (e.g. cached response, error).
    let printed = false;
    const doPrint = () => {
      if (printed) return;
      printed = true;
      try { win.focus(); } catch { /* focus best-effort */ }
      win.print();
    };
    img.onload = doPrint;
    img.onerror = doPrint;
    win.setTimeout(doPrint, 1500);
    label.appendChild(img);

    if (serial) {
      const s = doc.createElement("div");
      s.className = "serial";
      s.textContent = `SN · ${serial}`;
      label.appendChild(s);
    }

    const scan = doc.createElement("div");
    scan.className = "serial";
    scan.textContent = "Scan to open entry";
    label.appendChild(scan);

    doc.body.appendChild(label);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0A192F]/40 flex items-center justify-center p-4"
      data-testid="share-qr-dialog"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-[#E0DDD6] max-w-lg w-full"
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-[#E0DDD6]">
          <h2 className="font-serif-display text-2xl text-[#0A192F]">Ledger Tools</h2>
          <button onClick={onClose} data-testid="close-share-dialog"><X size={18} /></button>
        </header>

        {assetId && (
          <nav className="grid grid-cols-2 border-b border-[#E0DDD6]">
            <button
              data-testid="tab-qr"
              onClick={() => setMode("qr")}
              className={`py-3 text-sm font-medium border-r border-[#E0DDD6] ${mode === "qr" ? "bg-[#1A3622] text-[#F9F8F6]" : "text-[#5C5F58] hover:text-[#0A192F]"}`}
            >
              <QrCode size={14} weight="regular" className="inline mr-2" />
              QR Asset Tag
            </button>
            <button
              data-testid="tab-share"
              onClick={() => setMode("share")}
              className={`py-3 text-sm font-medium ${mode === "share" ? "bg-[#0A192F] text-[#F9F8F6]" : "text-[#5C5F58] hover:text-[#0A192F]"}`}
            >
              Heirs Share Link
            </button>
          </nav>
        )}

        {/* QR panel */}
        {(mode === "qr" && assetId) && (
          <div className="p-6 space-y-5" data-testid="qr-panel">
            <p className="text-sm text-[#5C5F58]">
              Print this tag and affix it to the object. Scanning reveals the full ledger entry — no digging through files, no hunting for serial stickers.
            </p>
            <div className="bg-[#F9F8F6] border border-[#E0DDD6] p-6 flex flex-col items-center gap-3">
              <img
                src={qrUrl}
                alt="QR code"
                className="w-[220px] h-[220px]"
                data-testid="qr-image"
              />
              <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] text-center">
                {assetName}
                {serial ? ` · SN ${serial}` : ""}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={printQr}
                data-testid="print-qr"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-4 py-2.5 text-sm border border-[#0F2818] hover:bg-[#0F2818]"
              >
                <Printer size={14} weight="bold" /> Print Tag
              </button>
              <button
                onClick={() => copy(publicAssetUrl)}
                data-testid="copy-asset-link"
                className="inline-flex items-center gap-2 px-4 py-2.5 text-sm border border-[#E0DDD6] bg-white hover:border-[#0A192F]"
              >
                <Copy size={14} /> Copy Link
              </button>
            </div>
          </div>
        )}

        {/* Share panel */}
        {(mode === "share" || !assetId) && (
          <div className="p-6 space-y-4" data-testid="share-panel">
            <p className="text-sm text-[#5C5F58]">
              Generate a read-only link for family members, executors, or attorneys. They see the ledger, valuations, and dispensation directives — but cannot edit, delete, or add entries. Revoke anytime.
            </p>
            {!share ? (
              <button
                onClick={createShare}
                disabled={generating}
                data-testid="generate-share"
                className="w-full bg-[#0A192F] text-[#F9F8F6] px-4 py-3 text-sm hover:bg-[#0F2818] disabled:opacity-60"
              >
                {generating ? "Generating…" : "Generate Heirs Link"}
              </button>
            ) : (
              <div className="space-y-3" data-testid="share-result">
                <div className="border border-[#E0DDD6] bg-[#F9F8F6] p-3">
                  <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2">
                    Read-only Link
                  </div>
                  <div className="flex gap-2">
                    <input
                      readOnly
                      value={`${window.location.origin}/heirs/${share.token}`}
                      className="flex-1 bg-white border border-[#E0DDD6] px-3 py-2 text-xs font-mono-ledger focus:outline-none"
                      data-testid="share-url"
                    />
                    <button
                      onClick={() => copy(`${window.location.origin}/heirs/${share.token}`)}
                      className="px-3 py-2 text-xs bg-[#0A192F] text-[#F9F8F6] hover:bg-[#0F2818]"
                      data-testid="copy-share-url"
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                </div>
                <p className="text-xs text-[#5C5F58] italic">
                  Save this link somewhere your heirs can find it — e.g., alongside your will or in a sealed envelope. It cannot be regenerated from the ledger UI, only created anew and revoked.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
